/**
 * Line sorter — pure, deterministic line sorting.
 * Safe to render server-side.
 */

export type SortMode = "alpha" | "numeric" | "length";

export interface SortOptions {
  mode?: SortMode;
  reverse?: boolean;
  /** Case-insensitive comparison (alpha mode). */
  caseInsensitive?: boolean;
  /** Drop blank/whitespace-only lines before sorting. */
  removeEmpty?: boolean;
}

export interface SortResult {
  text: string;
  count: number;
}

/** Parse a leading number from a line, or NaN if none. */
function leadingNumber(s: string): number {
  const m = /^\s*(-?\d+(?:\.\d+)?)/.exec(s);
  return m ? Number(m[1]) : Number.NaN;
}

export function sortLines(text: string, opts: SortOptions = {}): SortResult {
  const mode = opts.mode ?? "alpha";
  let lines = text.split("\n");
  if (opts.removeEmpty) lines = lines.filter((l) => l.trim() !== "");

  const sorted = [...lines];
  sorted.sort((a, b) => {
    if (mode === "length") return a.length - b.length;
    if (mode === "numeric") {
      const na = leadingNumber(a);
      const nb = leadingNumber(b);
      const aNaN = Number.isNaN(na);
      const bNaN = Number.isNaN(nb);
      if (aNaN && bNaN) return 0;
      if (aNaN) return 1; // non-numeric lines sort last
      if (bNaN) return -1;
      return na - nb;
    }
    const ca = opts.caseInsensitive ? a.toLowerCase() : a;
    const cb = opts.caseInsensitive ? b.toLowerCase() : b;
    return ca.localeCompare(cb);
  });

  if (opts.reverse) sorted.reverse();
  return { text: sorted.join("\n"), count: sorted.length };
}
